import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
    InsuranceCardScanResult,
    scanInsuranceCardApi,
} from '../../Service/Cousult.service'
import './InsuranceCardScanner.scss'

type Side = 'front' | 'back'

type Props = {
    onScanned: (fields: InsuranceCardScanResult) => void
    // Fires with a compact JPEG data URL for the given side (or null when
    // cleared) so the parent can attach copies to the patient's record.
    onImageCaptured?: (side: Side, dataUrl: string | null) => void
    disabled?: boolean
}

// Downscale a card photo to a compact JPEG data URL. Keeps the payload small
// enough to ride along in the JSON booking request while staying legible.
async function downscaleToJpegDataUrl(file: File, maxDim = 1400, quality = 0.82): Promise<string> {
    const readAsDataUrl = (f: File) =>
        new Promise<string>((resolve, reject) => {
            const fr = new FileReader()
            fr.onload = () => resolve(fr.result as string)
            fr.onerror = () => reject(new Error('read failed'))
            fr.readAsDataURL(f)
        })

    const original = await readAsDataUrl(file)
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const i = new Image()
        i.onload = () => resolve(i)
        i.onerror = () => reject(new Error('decode failed'))
        i.src = original
    })

    let { width, height } = img
    const longest = Math.max(width, height)
    if (longest > maxDim) {
        const scale = maxDim / longest
        width = Math.round(width * scale)
        height = Math.round(height * scale)
    }

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) return original
    ctx.drawImage(img, 0, 0, width, height)
    return canvas.toDataURL('image/jpeg', quality)
}

type Status =
    | { kind: 'idle' }
    | { kind: 'scanning' }
    | { kind: 'success'; confidence: InsuranceCardScanResult['confidence']; filledCount: number }
    | { kind: 'captured' }
    | { kind: 'error'; message: string }

const MAX_BYTES = 8 * 1024 * 1024
const ACCEPT = 'image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif'

const SIDE_COPY: Record<Side, { title: string; sub: string; previewLabel: string }> = {
    front: {
        title: 'Front of card',
        sub: "We'll read the front and fill the fields below — you can edit anything we get wrong.",
        previewLabel: 'Insurance card front preview',
    },
    back: {
        title: 'Back of card',
        sub: 'Optional — attached to your record along with the front.',
        previewLabel: 'Insurance card back preview',
    },
}

function countFilled(fields: InsuranceCardScanResult): number {
    return (
        ['insurance_carrier', 'policy_number', 'group_number', 'policy_holder_name', 'policy_holder_dob'] as const
    ).filter((k) => Boolean(fields[k])).length
}

type CardSideProps = {
    side: Side
    runOcr: boolean
    disabled?: boolean
    onScanned: (fields: InsuranceCardScanResult) => void
    onImageCaptured?: (side: Side, dataUrl: string | null) => void
}

const CardSide: React.FC<CardSideProps> = ({ side, runOcr, disabled, onScanned, onImageCaptured }) => {
    const fileInputRef = useRef<HTMLInputElement | null>(null)
    const cameraInputRef = useRef<HTMLInputElement | null>(null)
    const [previewUrl, setPreviewUrl] = useState<string | null>(null)
    const [status, setStatus] = useState<Status>({ kind: 'idle' })
    const [isDragging, setIsDragging] = useState(false)

    useEffect(() => {
        return () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl)
        }
    }, [previewUrl])

    const reset = useCallback(() => {
        setStatus({ kind: 'idle' })
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl)
            setPreviewUrl(null)
        }
        if (fileInputRef.current) fileInputRef.current.value = ''
        if (cameraInputRef.current) cameraInputRef.current.value = ''
        onImageCaptured?.(side, null)
    }, [previewUrl, onImageCaptured, side])

    const validateAndCapture = useCallback(
        async (file: File) => {
            if (!file.type.startsWith('image/')) {
                setStatus({ kind: 'error', message: 'Please upload an image file (JPG, PNG, or HEIC).' })
                return
            }
            if (file.size > MAX_BYTES) {
                setStatus({ kind: 'error', message: 'Image is larger than 8 MB. Please retake or compress.' })
                return
            }

            if (previewUrl) URL.revokeObjectURL(previewUrl)
            const url = URL.createObjectURL(file)
            setPreviewUrl(url)
            setStatus({ kind: 'scanning' })

            // Capture a compact copy up-front so it's available on submit even
            // if OCR finds nothing. Failure here must never block scanning.
            let captured: string | null = null
            try {
                captured = await downscaleToJpegDataUrl(file)
            } catch {
                captured = null
            }
            onImageCaptured?.(side, captured)

            if (!runOcr) {
                // Back side is image-only; there is nothing to OCR.
                setStatus(
                    captured
                        ? { kind: 'captured' }
                        : { kind: 'error', message: 'Could not process that image. Please try another photo.' }
                )
                return
            }

            try {
                const res = await scanInsuranceCardApi(file)
                const body = res.data
                if (!body?.success || !body.data) {
                    setStatus({ kind: 'error', message: body?.message || 'We could not read this card. Try a clearer photo.' })
                    return
                }
                const filled = countFilled(body.data)
                if (filled === 0) {
                    setStatus({
                        kind: 'error',
                        message: "We didn't find any insurance details on that image. Try a sharper, well-lit photo of the front of your card.",
                    })
                    return
                }
                onScanned(body.data)
                setStatus({ kind: 'success', confidence: body.data.confidence, filledCount: filled })
            } catch (e: any) {
                const msg =
                    e?.response?.data?.message ||
                    e?.message ||
                    'Something went wrong while scanning. Please try again.'
                setStatus({ kind: 'error', message: msg })
            }
        },
        [runOcr, onScanned, onImageCaptured, side, previewUrl]
    )

    const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) void validateAndCapture(file)
    }

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        setIsDragging(false)
        if (disabled) return
        const file = e.dataTransfer.files?.[0]
        if (file) void validateAndCapture(file)
    }

    const copy = SIDE_COPY[side]
    const busy = status.kind === 'scanning'
    const showStartOver =
        Boolean(previewUrl) || status.kind === 'error' || status.kind === 'success' || status.kind === 'captured'

    return (
        <div
            className={`insurance-card-scanner__side${isDragging ? ' is-dragging' : ''}`}
            onDragOver={(e) => {
                e.preventDefault()
                if (!disabled) setIsDragging(true)
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
        >
            <h5 className="insurance-card-scanner__side-title">{copy.title}</h5>
            <p className="insurance-card-scanner__sub">{copy.sub}</p>

            <div className="insurance-card-scanner__actions">
                <button
                    type="button"
                    className="insurance-card-scanner__btn insurance-card-scanner__btn--primary"
                    onClick={() => cameraInputRef.current?.click()}
                    disabled={disabled || busy}
                >
                    <span aria-hidden>📷</span> Use camera
                </button>
                <button
                    type="button"
                    className="insurance-card-scanner__btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={disabled || busy}
                >
                    <span aria-hidden>🖼️</span> Choose photo
                </button>
                {showStartOver && (
                    <button
                        type="button"
                        className="insurance-card-scanner__btn insurance-card-scanner__btn--ghost"
                        onClick={reset}
                        disabled={busy}
                    >
                        Start over
                    </button>
                )}
            </div>

            {(previewUrl || status.kind !== 'idle') && (
                <div className="insurance-card-scanner__preview">
                    {previewUrl && (
                        <div
                            className="insurance-card-scanner__thumb"
                            style={{ backgroundImage: `url(${previewUrl})` }}
                            aria-label={copy.previewLabel}
                        />
                    )}
                    <div className="insurance-card-scanner__status">
                        {status.kind === 'scanning' && (
                            <div className="insurance-card-scanner__progress">
                                <span className="insurance-card-scanner__spinner" /> Reading your card…
                            </div>
                        )}
                        {status.kind === 'success' && (
                            <div className="insurance-card-scanner__status--ok">
                                ✅ Filled {status.filledCount} field{status.filledCount === 1 ? '' : 's'}.{' '}
                                {status.confidence === 'high'
                                    ? 'Looks good — please double-check below.'
                                    : status.confidence === 'medium'
                                      ? 'Please review the fields below before submitting.'
                                      : 'Confidence is low — please verify each field carefully.'}
                            </div>
                        )}
                        {status.kind === 'captured' && (
                            <div className="insurance-card-scanner__status--ok">✅ Added to your record.</div>
                        )}
                        {status.kind === 'error' && (
                            <div className="insurance-card-scanner__status--error">⚠️ {status.message}</div>
                        )}
                    </div>
                </div>
            )}

            <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT}
                className="insurance-card-scanner__hidden-input"
                onChange={handleFileSelected}
                aria-hidden
                tabIndex={-1}
            />
            <input
                ref={cameraInputRef}
                type="file"
                accept={ACCEPT}
                capture="environment"
                className="insurance-card-scanner__hidden-input"
                onChange={handleFileSelected}
                aria-hidden
                tabIndex={-1}
            />
        </div>
    )
}

const InsuranceCardScanner: React.FC<Props> = ({ onScanned, onImageCaptured, disabled }) => {
    return (
        <div className="insurance-card-scanner">
            <div className="insurance-card-scanner__header">
                <div>
                    <h4 className="insurance-card-scanner__title">
                        <span aria-hidden>📇</span>
                        Scan your insurance card
                    </h4>
                    <p className="insurance-card-scanner__sub">
                        Add a photo of the <strong>front</strong> and <strong>back</strong> of your card. We'll read
                        the front to fill in the fields below and attach both to your record.
                    </p>
                </div>
                <span className="insurance-card-scanner__badge">Optional</span>
            </div>

            <div className="insurance-card-scanner__sides">
                <CardSide side="front" runOcr disabled={disabled} onScanned={onScanned} onImageCaptured={onImageCaptured} />
                <CardSide side="back" runOcr={false} disabled={disabled} onScanned={onScanned} onImageCaptured={onImageCaptured} />
            </div>

            <p className="insurance-card-scanner__privacy">
                <span aria-hidden>🔒</span>
                Your card images are sent securely to read your insurance details and are saved to your{' '}
                <strong>secure patient record</strong>.
            </p>
        </div>
    )
}

export default InsuranceCardScanner
